import * as Sentry from '@sentry/nextjs';

import type { SubscriptionTransport } from './subscription-transport';

/**
 * 購読の接続（WebSocket と SSE）の動きを、Sentry のブレッドクラムと、タイムアウト時の記録に残す。
 * Sentry の自動記録は fetch・クリック・console だけで、WebSocket は対象外のため、自前で残す。
 * どちらの転送方式かは、記録の `transport`（'ws' | 'sse'）で区別する。
 * 接続の試行は、WebSocket では接続を作るたび、SSE では購読の POST を送るたびに数える（再試行も 1 回と数える）。
 * 接続の試行回数とタイムアウト回数は、タブを開いてからの累計。時刻の差だけが、送信を起点にした値になる。
 */

// 短い英数字の語（'Normal Closure' など）だけを理由として残す
const PLAIN_CLOSE_REASON = /^[A-Za-z0-9 _.-]{1,40}$/;

export type WsEventName = 'connecting' | 'opened' | 'connected' | 'closed' | 'error' | 'connect_timeout';

export type WsEventData = Record<string, string | number | boolean | null> & { transport?: SubscriptionTransport };

type WsTimeline = {
  connectingAt: number | null;
  openedAt: number | null;
  connectedAt: number | null;
  lastCloseCode: number | null;
  lastTransport: SubscriptionTransport | null;
  connectAttempts: number;
  connectTimeouts: number;
};

const createEmptyTimeline = (): WsTimeline => ({
  connectingAt: null,
  openedAt: null,
  connectedAt: null,
  lastCloseCode: null,
  lastTransport: null,
  connectAttempts: 0,
  connectTimeouts: 0,
});

let timeline = createEmptyTimeline();

const WARNING_EVENTS: readonly WsEventName[] = ['error', 'connect_timeout'];

const applyEvent = (current: WsTimeline, name: WsEventName, data: WsEventData, now: number): WsTimeline => {
  switch (name) {
    case 'connecting':
      return {
        ...current,
        connectingAt: now,
        openedAt: null,
        connectedAt: null,
        connectAttempts: current.connectAttempts + 1,
      };
    case 'opened':
      return { ...current, openedAt: now };
    case 'connected':
      return { ...current, connectedAt: now };
    case 'closed':
      // 閉じた後のタイムアウトが、接続済みのように記録されないよう、開通の時刻を消す
      return {
        ...current,
        openedAt: null,
        connectedAt: null,
        lastCloseCode: typeof data.code === 'number' ? data.code : null,
      };
    case 'connect_timeout':
      return { ...current, connectTimeouts: current.connectTimeouts + 1 };
    default:
      return current;
  }
};

export function recordWsEvent(name: WsEventName, data: WsEventData = {}, now: number = Date.now()): void {
  timeline = applyEvent(timeline, name, data, now);
  if (data.transport !== undefined) {
    timeline = { ...timeline, lastTransport: data.transport };
  }

  // 観測の失敗で通信を止めない
  try {
    Sentry.addBreadcrumb({
      category: 'subscription',
      message: name,
      level: WARNING_EVENTS.includes(name) ? 'warning' : 'info',
      data,
    });
  } catch {
    // Sentry が使えない環境（テストなど）では、時刻の記録だけ残す
  }
}

/** close イベントから、記録に使う値だけを取り出す（本文は取らない）。 */
export function describeCloseEvent(event: unknown): WsEventData {
  if (typeof event !== 'object' || event === null) {
    return {};
  }
  const { code, reason, wasClean } = event as { code?: unknown; reason?: unknown; wasClean?: unknown };
  const closeInfo: WsEventData = {};
  if (typeof code === 'number') {
    closeInfo.code = code;
  }
  if (typeof reason === 'string') {
    // graphql-ws の理由には、受け取った payload の JSON が入ることがあるため、固定の語に近いものだけ残す
    if (PLAIN_CLOSE_REASON.test(reason)) {
      closeInfo.reason = reason;
    }
  }
  if (typeof wasClean === 'boolean') {
    closeInfo.wasClean = wasClean;
  }
  return closeInfo;
}

const offsetFrom = (sentAt: number | null, at: number | null): number | null =>
  sentAt !== null && at !== null ? at - sentAt : null;

/** 送信時刻を起点にした、接続の各時刻（ミリ秒）。接続が送信より前に始まっていれば負の値になる。 */
export function getWsTimelineSince(sentAt: number | null) {
  return {
    subscription_connect_attempts: timeline.connectAttempts,
    subscription_connect_timeouts: timeline.connectTimeouts,
    subscription_last_close_code: timeline.lastCloseCode,
    subscription_transport: timeline.lastTransport,
    subscription_connecting_offset_ms: offsetFrom(sentAt, timeline.connectingAt),
    subscription_opened_offset_ms: offsetFrom(sentAt, timeline.openedAt),
    subscription_connected_offset_ms: offsetFrom(sentAt, timeline.connectedAt),
  };
}

export function resetWsTelemetry(): void {
  timeline = createEmptyTimeline();
}
